import React, { SetStateAction, useState } from "react";

import {
	AdditionalInfoFieldType,
	AdditionalInfoType,
	Severity,
} from "node-library/dist/constants";
import {
	FrontendCompanySampleAdditionalInfoFields,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSample,
	FrontendSampleAdditionalInfoMapping,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendSampleTestInformation,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";
import {
	CommonSwitch,
	CustomDropdown,
	CustomTextInput,
} from "../../../common-components/inputs";

import { Card, Tooltip } from "@mui/material";
import { NeutralColor } from "node-library/dist/colors";
import {
	GreenButton,
	GreyButton,
	OutlinedButton,
} from "../../../common-components/button";
import { CollapsibleCard } from "../../../common-components/card";
import CommonAutoCompleteDropdown from "../../../common-components/commonAutoCompleteDropdown";
import { CustomDatePicker } from "../../../common-components/datePicker";
import CreateSpecificationDrawer from "../../../common-components/drawers/createSpecificationAndReadingDrawer";
import { AlertIcon, DeleteIcon } from "../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import { getTestCategoryMatrixMap } from "../../../helpers/testCategoryHelper";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import EditSampleModal from "../clientOrganizations/modal/editSampleModal";
import SelectSpecificationFileModal from "./components/selectSpecificationFileModal";
import { BLANK_SAMPLE_TEST_INFO } from "./samplePackages";
import CreateSpecificationAndReadingDrawer from "../../../common-components/drawers/createSpecificationAndReadingDrawer";
import {
	getBreadcrumbsBySpecFile,
	getSpecificationIcon,
} from "../../../helpers/specificationHelper";
import { getFileDisplayName } from "node-library/dist/helpers/file_helper";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;
	orderTestCategory: FrontendCompanyTestCategory;

	organizationId: string;
	organizationSamples: Array<FrontendSample>;
	setOrganizationSamples: React.Dispatch<
		React.SetStateAction<FrontendSample[]>
	>;
	//basic info
	lotCode: string;
	setLotCode: React.Dispatch<React.SetStateAction<string>>;
	sampleName: string;
	setSampleName: React.Dispatch<React.SetStateAction<string>>;
	sampleDescription: string;
	setSampleDescription: React.Dispatch<React.SetStateAction<string>>;

	selectedSampleId: string;
	setSelectedSampleId: React.Dispatch<React.SetStateAction<string>>;

	isCustomMatrix: boolean;

	matrix: string;
	setMatrix: React.Dispatch<React.SetStateAction<string>>;
	selectedMatrixId: string;
	setSelectedMatrixId: React.Dispatch<React.SetStateAction<string>>;

	sampleAdditionalInfos: FrontendSampleAdditionalInfoMapping[];
	setSampleAdditionalInfos: React.Dispatch<
		React.SetStateAction<FrontendSampleAdditionalInfoMapping[]>
	>;

	sampleCategory: string;

	isNewSample: boolean;
	setIsNewSample: React.Dispatch<React.SetStateAction<boolean>>;

	multipleMatchingSamples: boolean;

	sampleSpecifications: Array<FrontendSampleSpecification>;
	setSampleSpecifications: React.Dispatch<
		React.SetStateAction<FrontendSampleSpecification[]>
	>;

	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	setSpecDirectories: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecificationDirectory>>
	>;

	selectedSpecificationId: string | undefined;
	setSelectedSpecificationId: React.Dispatch<
		React.SetStateAction<string | undefined>
	>;

	setSampleTestInfo: React.Dispatch<
		React.SetStateAction<FrontendSampleTestInformation>
	>;
	setSelectedPackageId: React.Dispatch<React.SetStateAction<string>>;

	setCurrentPageIndex: (value: number) => void;
	discardSample: () => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function StepTwoSampleInfo(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		setCompany,
		user,
		organizationSamples,
		lotCode,
		setLotCode,
		orderTestCategory,
		sampleName,
		setSampleName,
		sampleDescription,
		setSampleDescription,
		multipleMatchingSamples,
		sampleSpecifications,
		setSampleSpecifications,
		specDirectories,
		setSpecDirectories,
		setCurrentPageIndex,
		setUnsavedChanges,
	} = props;
	const categoryMatrixMap: Map<
		string,
		Array<FrontendCompanySampleMatrix>
	> = getTestCategoryMatrixMap(props.orderTestCategory);

	const categorySamples: Array<FrontendSample> = organizationSamples.filter(
		(sample) => {
			return sample.testCategoryId === orderTestCategory.testCategoryId;
		},
	);

	const [selectedSampleId, setSelectedSampleId] = useState<string>(
		props.selectedSampleId,
	);
	const [isNewSample, setIsNewSample] = useState<boolean>(props.isNewSample);

	const [isCustomMatrix, setIsCustomMatrix] = useState<boolean>(
		props.isCustomMatrix,
	);
	const DEFAULT_MATRIX_ID: string =
		orderTestCategory.sampleMatrices.length > 0
			? orderTestCategory.sampleMatrices[0].matrixId
			: "";

	const [selectedMatrixId, setSelectedMatrixId] = useState<string>(
		props.selectedMatrixId || DEFAULT_MATRIX_ID,
	);

	const selectedMatrix: FrontendCompanySampleMatrix | undefined =
		orderTestCategory.sampleMatrices.find((matrix) => {
			return matrix.matrixId === selectedMatrixId;
		});

	const [matrix, setMatrix] = useState<string>(
		props.matrix || selectedMatrix?.matrix || "",
	);

	const sampleCategory: string = selectedMatrix
		? selectedMatrix.sampleCategory
		: "";

	const [sampleAdditionalInfos, setSampleAdditionalInfos] = useState<
		FrontendSampleAdditionalInfoMapping[]
	>(props.sampleAdditionalInfos || []);

	const [uploadSpecModalOpen, setUploadSpecModalOpen] =
		useState<boolean>(false);

	const [selectSpecModalOpen, setSelectSpecModalOpen] =
		useState<boolean>(false);
	const [attachSpecification, setAttachSpecification] = useState<boolean>(
		props.selectedSpecificationId ? true : false,
	);

	const [selectedSpecificationId, setSelectedSpecificationId] = useState<
		string | undefined
	>(props.selectedSpecificationId);

	const [editingSampleModalOpen, setEditingSampleModalOpen] =
		useState<boolean>(false);

	//field validations
	const [sampleNameErrorMsg, setSampleNameErrorMsg] = useState<string>("");
	const [lotCodeErrorMsg, setLotCodeErrorMsg] = useState<string>("");
	const [sampleDescriptionErrorMsg, setSampleDescriptionErrorMsg] =
		useState<string>("");

	const [sampleAdditionalInfosErrorMsg, setSampleAdditionalInfosErrorMsg] =
		useState<Array<{ id: string; message: string }>>([]);

	const [loading, setLoading] = useState<boolean>(false);

	const mandatoryFields: Array<any> = [
		{
			field: sampleName,
			predicates: [sampleName && sampleName.length > 0],
			errorMsgs: [translation("MissingInformation")],
			setError: setSampleNameErrorMsg,
		},
		{
			field: lotCode,
			predicates: [lotCode && lotCode.length > 0],
			errorMsgs: [translation("MissingInformation")],
			setError: setLotCodeErrorMsg,
		},

		{
			field: sampleDescription,
			predicates: [],
			errorMsgs: [],
			setError: setSampleDescriptionErrorMsg,
		},
	];

	const testCategorySampleAdditionalInfoFields: Array<React.ReactNode> =
		orderTestCategory.sampleAdditionalInfo
			.filter((info) => {
				return info.infoType === AdditionalInfoType.SAMPLE;
			})
			.map((info) => {
				const existingInfo = sampleAdditionalInfos.find((existing) => {
					return info.fieldId === existing.fieldId;
				});
				const value = existingInfo?.value || "";
				const setValueFunction = (value: string | Date | null) => {
					if (value == null) return;
					const updatedInfos: Array<FrontendSampleAdditionalInfoMapping> =
						sampleAdditionalInfos.map((existing) =>
							info.fieldId === existing.fieldId
								? { ...existing, value: value }
								: existing,
						);
					if (!existingInfo) {
						updatedInfos.push({
							fieldId: info.fieldId,
							value: value,
							infoType: AdditionalInfoType.SAMPLE_INSTANCE,
							additionalFieldName: info.additionalFieldName,
							fieldType: info.fieldType,
						});
					}
					setSampleAdditionalInfos(updatedInfos);
					setUnsavedChanges(true);
				};
				switch (info.fieldType) {
					case AdditionalInfoFieldType.TEXT:
						return (
							<CustomTextInput
								id={`new-sample-${info.additionalFieldName}-input`}
								label={info.additionalFieldName}
								value={value as string}
								setValue={setValueFunction}
								// description={translation("LotCodeDescription")}
								required={info.mandatory}
								errorMsg={
									sampleAdditionalInfosErrorMsg.find((msg) => {
										return msg.id === info.fieldId;
									})?.message || ""
								}
								setErrorMsg={(value) => {
									const updatedMsgs = sampleAdditionalInfosErrorMsg.map(
										(existing) =>
											info.fieldId === existing.id
												? { ...existing, message: value }
												: existing,
									);
									if (
										!sampleAdditionalInfosErrorMsg.find((msg) => {
											return msg.id === info.fieldId;
										})
									) {
										updatedMsgs.push({
											id: info.fieldId,
											message: value,
										});
									}
									setSampleAdditionalInfosErrorMsg(updatedMsgs);
								}}
							/>
						);
					case AdditionalInfoFieldType.DATE:
						return (
							<div className="modal-datepicker-row">
								<div className="typography-body-01">
									{info.additionalFieldName}
								</div>

								<CustomDatePicker
									selectedDate={new Date(value)}
									setSelectedDate={setValueFunction}
									errorMsg={
										sampleAdditionalInfosErrorMsg.find((msg) => {
											return msg.id === info.fieldId;
										})?.message || ""
									}
								></CustomDatePicker>
							</div>
						);
				}
			});

	const handleSubmitSampleInfo = async () => {
		// Simple validation for required fields
		let hasError: boolean = false;
		if (isCustomMatrix) {
			openSnackbar(
				translation("ObsoleteSampleMatrix"),
				Severity.ERROR,
				translation("ObsoleteSampleMatrixDescription"),
				<RedErrorIcon />,
			);
			return;
		}
		for (const field of mandatoryFields) {
			field.setError("");
			for (let i = 0; i < field.predicates.length; i++) {
				if (!field.predicates[i]) {
					hasError = true;
					field.setError(field.errorMsgs[i]);
					break;
				}
			}
		}
		//check mandatory additional fields
		const mandatoryAdditionalFields: FrontendCompanySampleAdditionalInfoFields[] =
			orderTestCategory.sampleAdditionalInfo.filter((info) => {
				return info.infoType === AdditionalInfoType.SAMPLE && info.mandatory;
			});
		for (const field of mandatoryAdditionalFields) {
			const matchingInfo = sampleAdditionalInfos.find((info) => {
				return field.fieldId === info.fieldId;
			});
			if (!matchingInfo || !matchingInfo.value) {
				//set error
				hasError = true;
				const updatedMsgs = sampleAdditionalInfosErrorMsg.map((existing) =>
					field.fieldId === existing.id
						? { ...existing, message: translation("MissingInformation") }
						: existing,
				);
				if (
					!sampleAdditionalInfosErrorMsg.find((msg) => {
						return msg.id === field.fieldId;
					})
				) {
					updatedMsgs.push({
						id: field.fieldId,
						message: translation("MissingInformation"),
					});
				}
				setSampleAdditionalInfosErrorMsg(updatedMsgs);
				break;
			}
		}

		if (hasError) {
			return;
		}
		if (attachSpecification && !selectedSpecificationId) {
			openSnackbar(
				translation("InvalidSpecificationFile"),
				Severity.ERROR,
				translation("InvalidSpecificationFileDescription"),
				<RedErrorIcon />,
			);
			return;
		}
		if (attachSpecification && !currentFolderSelectedSpec) {
			openSnackbar(
				translation("InvalidSpecificationFile"),
				Severity.ERROR,
				translation("InvalidSpecificationFileDescription"),
				<RedErrorIcon />,
			);
			return;
		}

		props.setSelectedSampleId(selectedSampleId);

		props.setIsNewSample(isNewSample);

		props.setMatrix(matrix);
		props.setSelectedMatrixId(selectedMatrixId);
		props.setSampleAdditionalInfos(sampleAdditionalInfos);
		props.setSelectedSpecificationId(
			attachSpecification ? selectedSpecificationId : undefined,
		);

		if (props.sampleCategory !== sampleCategory) {
			props.setSelectedPackageId("");

			props.setSampleTestInfo(BLANK_SAMPLE_TEST_INFO);
		}

		setCurrentPageIndex(2);
	};

	function selectSample(sampleId: string) {
		setSelectedSampleId(sampleId);
		setAttachSpecification(false);

		setSelectedSpecificationId(undefined);

		if (!sampleId) {
			setIsNewSample(true);
			if (isCustomMatrix) {
				setIsCustomMatrix(false);
				setSelectedMatrixId(DEFAULT_MATRIX_ID);
			}
		} else {
			const selectedSample: FrontendSample | undefined = categorySamples.find(
				(sample) => {
					return sample.sampleId === sampleId;
				},
			);
			if (selectedSample) {
				setSampleName(selectedSample.sampleName);
				setLotCode(selectedSample.lotCode);
				setSampleDescription(selectedSample.sampleDescription);
				setIsNewSample(selectedSample.isNewSample);
				setIsCustomMatrix(selectedSample.isCustomMatrix);
				if (selectedSample.isCustomMatrix) {
					setMatrix(selectedSample.matrix);
				} else {
					const sampleMatrix: FrontendCompanySampleMatrix | undefined =
						orderTestCategory.sampleMatrices.find((matrix) => {
							return matrix.matrixId === selectedSample.matrixId;
						});
					setMatrix(sampleMatrix?.matrix || "");
					setSelectedMatrixId(selectedSample.matrixId || "");
				}
				setSampleAdditionalInfos(selectedSample.sampleAdditionalInfos);
			}
		}
	}

	const orgSampleMap: { [key: string]: FrontendSample | undefined } =
		categorySamples.reduce(
			(acc, sample) => {
				acc[sample.sampleId] = sample;
				return acc;
			},
			{} as { [key: string]: FrontendSample },
		);

	const existingSampleSelector: React.ReactNode = (
		<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
			{multipleMatchingSamples && (
				<Tooltip
					PopperProps={{
						sx: {
							"& .MuiTooltip-tooltip": {
								backgroundColor: NeutralColor.NEUTRAL_GREY_950,
								padding: "12px 16px",
							},
						},
					}}
					title={`${translation("MultipleMatchingSampleWarning")}`}
					arrow
				>
					<span style={{ display: "flex", width: "fit-content" }}>
						<AlertIcon></AlertIcon>
					</span>
				</Tooltip>
			)}
			<div style={{ width: "100%" }}>
				<CommonAutoCompleteDropdown
					id="select-existing-sample-autocomplete"
					options={[
						...categorySamples.map((sample) => {
							return sample.sampleId;
						}),
						"",
					]}
					value={selectedSampleId}
					setValue={(sampleId) => {
						selectSample(sampleId);
					}}
					getOptionLabel={(sampleId) => {
						const sample = orgSampleMap[sampleId];
						if (!sample) {
							return `--${translation("NewSample")}--`;
						} else if (sample.isNewSample) {
							return `(${translation("NewSample")}) - ${sample.lotCode} - ${
								sample.sampleName
							}`;
						}
						return `${sample.fullSampleId} - ${sample.lotCode} - ${sample.sampleName}`;
					}}
				/>
			</div>
		</div>
	);
	const nameAndLotcodeInput: React.ReactNode = (
		<div className="sample-info-name-lotcode-row">
			<CustomTextInput
				id="new-sample-name-input"
				label={translation("SampleName")}
				value={sampleName}
				setValue={(value) => {
					setSampleName(value);
					setUnsavedChanges(true);
				}}
				disabled={selectedSampleId ? true : false}
				placeholder={translation("SampleNameDescription")}
				required
				errorMsg={sampleNameErrorMsg}
				setErrorMsg={setSampleNameErrorMsg}
			/>
			<CustomTextInput
				id="new-sample-lotcode-input"
				label={translation("LotCode")}
				value={lotCode}
				setValue={(value) => {
					setLotCode(value);
					setUnsavedChanges(true);
				}}
				// description={translation("LotCodeDescription")}
				disabled={selectedSampleId ? true : false}
				placeholder={translation("LotCodeDescription")}
				required
				errorMsg={lotCodeErrorMsg}
				setErrorMsg={setLotCodeErrorMsg}
			/>
		</div>
	);
	const sampleAdditionalInfoInput: React.ReactNode = (
		<div className="sample-info-name-lotcode-row">
			{testCategorySampleAdditionalInfoFields}
		</div>
	);
	function handleMatrixChange(matrix: string) {
		const matchingMatrixEntries:
			| Array<FrontendCompanySampleMatrix>
			| undefined = categoryMatrixMap.get(matrix);

		if (!matchingMatrixEntries) return;
		setMatrix(matrix);
		setSelectedMatrixId(matchingMatrixEntries[0]?.matrixId);

		setUnsavedChanges(true);
		setAttachSpecification(false);
		setSelectedSpecificationId(undefined);
	}

	const customMatrixField = {
		label: `${translation("CustomMatrix")}:`,
		description: ``,
		inputType: "checkbox",
		checked: isCustomMatrix,
		setChecked: () => {},
	};
	const customMatrixSwitch: React.ReactNode = (
		<div className="common-card-header" style={{ margin: "10px 0px" }}>
			<div className="typography-heading-03" style={{ display: "flex" }}>
				{isCustomMatrix ? (
					<Tooltip
						PopperProps={{
							sx: {
								"& .MuiTooltip-tooltip": {
									backgroundColor: NeutralColor.NEUTRAL_GREY_950,
									padding: "12px 16px",
								},
							},
						}}
						title={`${translation("CustomMatrixWarning")}`}
						arrow
					>
						<span style={{ display: "flex", width: "fit-content" }}>
							<AlertIcon></AlertIcon>
						</span>
					</Tooltip>
				) : (
					<></>
				)}
				{translation("CustomMatrix")}
			</div>
			<CommonSwitch
				id={"additional-test-switch"}
				checked={customMatrixField.checked}
				setChecked={customMatrixField.setChecked}
				disabled
			/>
		</div>
	);

	const matrixSelector: React.ReactNode = isCustomMatrix ? (
		<CustomTextInput
			id={`custom-matrix-textbox`}
			key={`custom-matrix-textbox`}
			label={translation("Matrix")}
			value={matrix}
			setValue={setMatrix}
			type="text"
			required={true}
			disabled
		></CustomTextInput>
	) : (
		<CustomDropdown
			id="matrix-dropdown"
			label={translation("Matrix")}
			options={Array.from(categoryMatrixMap.keys()).map((value) => {
				return {
					value: value,
					title: value,
				};
			})}
			disabled={selectedSampleId ? true : false}
			value={matrix}
			setValue={handleMatrixChange}
		></CustomDropdown>
	);

	const subMatrixOptions: Array<{
		value: string;
		title: string;
	}> = isCustomMatrix
		? []
		: (categoryMatrixMap.get(matrix) || []).map((value) => {
				const split = value.subMatrix.split(`${matrix} -`);

				const subMatrixTitle: string =
					split.length > 1 ? split[1].trim() : value.subMatrix;
				return {
					value: value.matrixId,
					title: subMatrixTitle,
				};
			});

	const subMatrixSelector: React.ReactNode = isCustomMatrix ? (
		<> </>
	) : (
		<CustomDropdown
			id="sub-matrix-dropdown"
			label={translation("SubMatrix")}
			options={subMatrixOptions}
			value={selectedMatrixId}
			setValue={(value) => {
				setSelectedMatrixId(value);
				setAttachSpecification(false);
				setSelectedSpecificationId(undefined);
				setUnsavedChanges(true);
			}}
			disabled={selectedSampleId ? true : false}
		></CustomDropdown>
	);

	const matrixSelectionRow: React.ReactNode = (
		<div>
			{customMatrixSwitch}
			<div key={"matrix-weight"} className="new-sample-matrix-selection-row">
				{matrixSelector}
				{subMatrixSelector}
			</div>
		</div>
	);

	const sampleDescriptionInput: React.ReactNode = (
		<CustomTextInput
			id="new-sample-weight-input"
			key="new-sample-weight-input"
			label={`${translation("SampleDescription")}`}
			value={sampleDescription}
			setValue={(value) => {
				setSampleDescription(value);
				setUnsavedChanges(true);
			}}
			placeholder={translation("SampleDescriptionDescription")}
			disabled={selectedSampleId ? true : false}
			required
			isTextArea
			errorMsg={sampleDescriptionErrorMsg}
			setErrorMsg={setSampleDescriptionErrorMsg}
		/>
	);

	const currentFolderSpecs = sampleSpecifications.filter((spec) => {
		return spec.matrixId === selectedMatrixId;
	});

	const currentFolderSelectedSpec = currentFolderSpecs.find((spec) => {
		return spec.specificationId == selectedSpecificationId;
	});
	const attachSpecificationSwitch: React.ReactNode = (
		<div className="common-card-header">
			<div>
				<label className="typography-body-01">
					{translation("AttachSpecificationFile")}{" "}
				</label>
				<div
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					className="typography-caption"
				>
					{translation("AttachSpecificationFileDescription", {
						count: currentFolderSpecs.length,
					})}
				</div>
			</div>

			<CommonSwitch
				id={"attach-sample-spec-switch"}
				checked={attachSpecification}
				setChecked={(value) => {
					setAttachSpecification(value);
					setUnsavedChanges(true);
				}}
			/>
		</div>
	);

	const attachSpecificationCollapseContent: React.ReactNode = (
		<div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					gap: "10px",
					flexWrap: "wrap",
				}}
			>
				<div className="typography-body-02" style={{ marginTop: "10px" }}>
					{selectedSpecificationId ? (
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: "5px",
							}}
						>
							<div
								style={{
									wordWrap: "break-word",
									display: "flex",
									alignItems: "center",
									gap: "5px",
								}}
							>
								{!currentFolderSelectedSpec ||
								!currentFolderSelectedSpec.active ? (
									<Tooltip
										PopperProps={{
											sx: {
												"& .MuiTooltip-tooltip": {
													backgroundColor: NeutralColor.NEUTRAL_GREY_950,
													padding: "12px 16px",
												},
											},
										}}
										title={translation("SpecificationDeletedError")}
										arrow
									>
										<span style={{ display: "flex", width: "fit-content" }}>
											<AlertIcon></AlertIcon>
										</span>
									</Tooltip>
								) : (
									<></>
								)}
								{currentFolderSelectedSpec &&
									getSpecificationIcon(currentFolderSelectedSpec, translation)}
								{currentFolderSelectedSpec
									? `	${[
											...getBreadcrumbsBySpecFile(
												currentFolderSelectedSpec,
												specDirectories,
											).map((folder) => {
												return folder.name;
											}),
											`${getFileDisplayName(currentFolderSelectedSpec.fileName, currentFolderSelectedSpec.format)}`,
										].join("/")}`
									: ``}
							</div>
							<div
								style={{ cursor: "pointer" }}
								onClick={() => {
									setSelectedSpecificationId(undefined);
								}}
							>
								<DeleteIcon></DeleteIcon>
							</div>
						</div>
					) : (
						translation("NoFileSelected")
					)}
				</div>
				<div>
					<OutlinedButton
						id="upload-specification-button"
						size="small"
						children={translation("Upload")}
						onClick={() => {
							setUploadSpecModalOpen(true);
						}}
						outlineColor={NeutralColor.NEUTRAL_GREY_950}
					></OutlinedButton>
					<OutlinedButton
						id="select-specification-button"
						size="small"
						children={translation("Select")}
						onClick={() => {
							setSelectSpecModalOpen(true);
						}}
						outlineColor={NeutralColor.NEUTRAL_GREY_950}
					></OutlinedButton>
				</div>
			</div>
		</div>
	);

	const sampleAttachmentSwitchContainer: React.ReactNode = isCustomMatrix ? (
		<></>
	) : (
		<CollapsibleCard
			className="sample-info-collapsible-card"
			expanded={attachSpecification}
			collapsibleContent={attachSpecificationCollapseContent}
			content={attachSpecificationSwitch}
			collapsibleDivider
		></CollapsibleCard>
	);

	async function editSample(updatedSample: FrontendSample) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest("/samples/update-sample", {
				organizationId: props.organizationId,
				sampleId: updatedSample.sampleId,
				sampleName: updatedSample.sampleName.trim(),
				lotCode: updatedSample.lotCode.trim(),
				sampleDescription: updatedSample.sampleDescription.trim(),
				matrix: updatedSample.matrix,
				matrixId: updatedSample.matrixId,
				isCustomMatrix: updatedSample.isCustomMatrix,
				frontendAdditionalInfo: updatedSample.sampleAdditionalInfos,
			});
			props.setOrganizationSamples((prevSamples) =>
				prevSamples.map((sample) =>
					sample.sampleId === updatedSample.sampleId ? updatedSample : sample,
				),
			);
			setEditingSampleModalOpen(false);

			setSampleName(updatedSample.sampleName);
			setLotCode(updatedSample.lotCode);
			setSampleDescription(updatedSample.sampleDescription);
			setIsNewSample(updatedSample.isNewSample);
			setIsCustomMatrix(updatedSample.isCustomMatrix);
			if (updatedSample.isCustomMatrix) {
				setMatrix(updatedSample.matrix);
			} else {
				setSelectedMatrixId(updatedSample.matrixId || "");
			}
			setSampleAdditionalInfos(updatedSample.sampleAdditionalInfos);

			openSnackbar(
				translation("SampleUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
		} catch (error) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	const editSampleModal: React.ReactNode = (
		<EditSampleModal
			selectedTestCategory={orderTestCategory}
			editSample={editSample}
			open={editingSampleModalOpen}
			setOpen={setEditingSampleModalOpen}
			sample={
				categorySamples.find((sample) => {
					return sample.sampleId === selectedSampleId;
				}) as FrontendSample
			}
			loading={loading}
			setLoading={setLoading}
		></EditSampleModal>
	);
	const editSampleButton: React.ReactNode = (
		<GreyButton
			children={translation("EditSample")}
			id="edit-sample-button"
			onClick={() => setEditingSampleModalOpen(true)}
			disabled={isNewSample}
		></GreyButton>
	);

	const stepTwoSampleInfoComponent: JSX.Element = (
		<>
			<div>
				<Card className="common-card card-border-radius-24p">
					<div className="step-two-info-container">
						<div className="typography-heading-03">
							{translation("NewSampleStepTwoDescription")}
						</div>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "20px",
								marginTop: "20px",
							}}
						>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "7fr 1fr",
									gap: "10px",
								}}
							>
								{existingSampleSelector}
								{editSampleButton}
							</div>
							{nameAndLotcodeInput}
							{sampleAdditionalInfoInput}
							{matrixSelectionRow}
							{sampleDescriptionInput}
							{sampleAttachmentSwitchContainer}
						</div>
					</div>
				</Card>
				<div className="add-sample-form-button-panel">
					<GreyButton
						children={translation("Discard")}
						id="back-button"
						onClick={() => props.discardSample()}
					></GreyButton>
					<GreenButton
						children={translation("Next")}
						id="submit-sample-info-button"
						type="submit"
						onClick={() => handleSubmitSampleInfo()}
					></GreenButton>
				</div>
			</div>
			{editSampleModal}
			{isCustomMatrix ? (
				<></>
			) : (
				<>
					<CreateSpecificationAndReadingDrawer
						company={company}
						setCompany={setCompany}
						user={user}
						open={uploadSpecModalOpen}
						setOpen={setUploadSpecModalOpen}
						testCategory={props.orderTestCategory}
						selectedMatrix={selectedMatrix as FrontendCompanySampleMatrix}
						currentFolderSpecifications={currentFolderSpecs}
						setFolderSpecifications={setSampleSpecifications}
						setSelectedSpecificationId={(id: string) => {
							setSelectedSpecificationId(id);
							setUnsavedChanges(true);
						}}
						organizationId={props.organizationId}
						specDirectories={specDirectories}
						setSpecDirectories={setSpecDirectories}
					></CreateSpecificationAndReadingDrawer>

					<SelectSpecificationFileModal
						user={user}
						open={selectSpecModalOpen}
						setOpen={setSelectSpecModalOpen}
						orderTestCategory={orderTestCategory}
						selectedMatrix={selectedMatrix as FrontendCompanySampleMatrix}
						specDirectories={specDirectories}
						currentFolderSpecifications={currentFolderSpecs}
						selectedSpecificationId={selectedSpecificationId}
						setSelectedSpecificationId={setSelectedSpecificationId}
					/>
				</>
			)}
		</>
	);

	return <div style={{ margin: "0 auto" }}>{stepTwoSampleInfoComponent}</div>;
}
